import {
  VIEW_TARGETS,
  type ViewRecord,
  type ViewTarget,
} from "@/shared/domain/views";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";
import {
  RobotoDomainException,
  RobotoNotFoundException,
  RobotoUnauthorizedException,
} from "@/shared/http/exceptions";
import type { ResourceTableViewingContext } from "@/shared/services";

import { asString, type ProposalVerdict } from "./collectionToolInput";

/**
 * Shared reading of the raw LLM params for `client_apply_view`.
 *
 * As with the collection tools, the confirmation card and `execute` share the
 * *verdict* on a proposal: `coerceApplyViewInput` returns either the payload
 * both act on or the one reason the call will be refused, so what is
 * approvable is what is sendable.
 *
 * Creating, changing, and deleting Views are server tools (`create_view`,
 * `update_view`, `delete_view`), so every surface that runs the agent can
 * write Views. Opening one on screen is the browser's job, which is this
 * tool's.
 */

/** Raw params of `client_apply_view`. See {@link CreateViewParams}. */
export interface ApplyViewParams {
  view_id?: unknown;
  name?: unknown;
  target?: unknown;
  set_as_default?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Whether a proposal is a JSON object at all; everything else is reported by the coercion. */
export function isApplyViewParams(raw: unknown): raw is ApplyViewParams {
  return isRecord(raw);
}

/**
 * How `client_apply_view` will find the View to open.
 *
 * An ID names one View outright. A name is only a way to find one, and a
 * View's name means nothing off its own table, so a name lookup always carries
 * the table it is searched on.
 */
export type ViewLookup = (
  | { by: "id"; viewId: string }
  | { by: "name"; name: string; target: ViewTarget }
) & {
  /** Also make the View the user's default for its table once opened. */
  setAsDefault: boolean;
};

function isPresent(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function asViewTarget(value: unknown): ViewTarget | undefined {
  return VIEW_TARGETS.find((candidate) => candidate === value);
}

/**
 * Reading of a `client_apply_view` proposal shared by its card and `execute`.
 *
 * An ID wins over a name given alongside it. A name is looked up on `target`
 * when the model names one, otherwise on the table on screen; with neither
 * there is no table to search, and that is refused here rather than after a
 * request. Names are matched by `execute`, not here: the card shows what will
 * be searched for, and the listing is a request the card must not make.
 */
export function coerceApplyViewInput(
  params: ApplyViewParams,
  tableOnScreen?: ResourceTableViewingContext,
): ProposalVerdict<ViewLookup> {
  let setAsDefault = false;
  if (isPresent(params.set_as_default)) {
    if (typeof params.set_as_default !== "boolean") {
      return {
        ok: false,
        reason: `set_as_default must be true or false; got ${JSON.stringify(params.set_as_default)}.`,
      };
    }
    setAsDefault = params.set_as_default;
  }

  const viewId = asString(params.view_id);
  if (viewId !== undefined) {
    return { ok: true, payload: { by: "id", viewId, setAsDefault } };
  }

  const name = asString(params.name);
  if (name === undefined) {
    return {
      ok: false,
      reason:
        "Name the View to open: give its view_id (e.g. 'vw_abc123'), or its " +
        "name.",
    };
  }

  let target: ViewTarget | undefined;
  if (isPresent(params.target)) {
    target = asViewTarget(params.target);
    if (target === undefined) {
      return {
        ok: false,
        reason:
          `target must be one of ${VIEW_TARGETS.join(", ")}; got ` +
          `${JSON.stringify(params.target)}.`,
      };
    }
  } else {
    // A list registers only the six View tables, so the narrowing is for the
    // type; a table that could not hold a View reads as no table.
    target = asViewTarget(tableOnScreen?.target);
    if (target === undefined) {
      return {
        ok: false,
        reason:
          `No resource table is on screen to look "${name}" up on. Give ` +
          "target (the table the View searches) or its view_id instead.",
      };
    }
  }

  return { ok: true, payload: { by: "name", name, target, setAsDefault } };
}

/**
 * The path that opens a View's own table with the View applied.
 *
 * A View's target names its list page's path, and `?view=` is what the list's
 * route loader and deep-link binding read.
 */
export function viewListPath(
  view: Pick<ViewRecord, "target" | "view_id">,
): string {
  return `/${view.target}?${VIEW_SEARCH_PARAM}=${view.view_id}`;
}

/**
 * What went wrong with a View request, in terms safe to store.
 *
 * Same reasoning as `describeCollectionFailure`: the service embeds the
 * caller's user ID in its authorization messages, and a tool result lives in
 * the thread transcript permanently.
 */
export function describeViewFailure(error: unknown): string {
  if (error instanceof RobotoUnauthorizedException) {
    return "the caller is not authorized to make this change";
  }
  if (error instanceof RobotoNotFoundException) {
    return "it does not exist, or is not visible to this org";
  }
  if (error instanceof RobotoDomainException) {
    return error.isClientError()
      ? `the service rejected the request (${error.name})`
      : `the service could not complete the request (${error.name})`;
  }
  return "the request did not complete";
}
